import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, MapPin, ChevronRight, SlidersHorizontal,
  X, GitCompare, Check
} from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import {
  Card, Input, StatusDot, PageHeader, Button, ReputationBadge
} from "../components/Primitives";
import { listProducers } from "../lib/db";

function FilterGroup({ title, items, active, keyName, onToggle }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-2">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => (
          <button
            key={it}
            onClick={() => onToggle(keyName, it)}
            data-testid={`filter-${keyName}-${it}`}
            className={`px-2.5 py-1 rounded-full text-[11px] border transition ${
              active.includes(it)
                ? "bg-electric-500 border-electric-500 text-white"
                : "bg-carbon-800 border-carbon-600 text-ink-700 hover:border-electric-500/50"
            }`}
          >
            {active.includes(it) && <Check className="w-3 h-3 inline mr-1" />}
            {it}
          </button>
        ))}
      </div>
    </div>
  );
}

const AVAILABILITIES = ["available", "limited", "unavailable"];

export default function Producers() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [producers, setProducers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({
    skills: [], materials: [], certs: [],
    location: null, availability: null, minRep: 0,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [compare, setCompare] = useState([]);

  useEffect(() => {
    let alive = true;
    listProducers().then((rows) => {
      if (alive) { setProducers(rows); setLoading(false); }
    }).catch(() => setLoading(false));
    return () => { alive = false; };
  }, []);

  const facets = useMemo(() => ({
    skills: Array.from(new Set(producers.flatMap((p) => p.skills))).sort(),
    materials: Array.from(new Set(producers.flatMap((p) => p.materials))).sort(),
    certs: Array.from(new Set(producers.flatMap((p) => p.certifications))).sort(),
    locations: Array.from(new Set(producers.map((p) => (p.location || "").split(",")[0].trim()).filter(Boolean))).sort(),
  }), [producers]);

  const toggleIn = (key, val) => setFilters((f) => ({
    ...f,
    [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
  }));

  const reset = () => setFilters({ skills: [], materials: [], certs: [], location: null, availability: null, minRep: 0 });

  const toggleCompare = (id) => setCompare((c) => (
    c.includes(id) ? c.filter((x) => x !== id) : c.length < 3 ? [...c, id] : c
  ));

  const list = useMemo(() => {
    let r = [...producers];
    if (query) {
      const q = query.toLowerCase();
      r = r.filter((p) =>
        (p.name || "").toLowerCase().includes(q) ||
        p.positioning.toLowerCase().includes(q) ||
        p.skills.some((s) => s.toLowerCase().includes(q)) ||
        p.materials.some((m) => m.toLowerCase().includes(q)) ||
        p.tags.some((tag) => tag.toLowerCase().includes(q))
      );
    }
    if (filters.skills.length) r = r.filter((p) => filters.skills.every((s) => p.skills.includes(s)));
    if (filters.materials.length) r = r.filter((p) => filters.materials.every((m) => p.materials.includes(m)));
    if (filters.certs.length) r = r.filter((p) => filters.certs.every((c) => p.certifications.includes(c)));
    if (filters.location) r = r.filter((p) => p.location.includes(filters.location));
    if (filters.availability) r = r.filter((p) => p.availability === filters.availability);
    if (filters.minRep > 0) r = r.filter((p) => p.reputation >= filters.minRep);
    return r;
  }, [query, filters, producers]);

  const activeFilterCount =
    filters.skills.length + filters.materials.length + filters.certs.length +
    (filters.location ? 1 : 0) + (filters.availability ? 1 : 0) + (filters.minRep > 0 ? 1 : 0);

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto animate-fade-up">
      <PageHeader
        title={t("producers.title")}
        subtitle={`${list.length} ${t("producers.results")} · ${t("producers.subtitle")}`}
        testId="producers-header"
        right={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters"
              className="relative"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {t("producers.filters")}
              {activeFilterCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-electric-500 text-white text-[10px] font-mono">{activeFilterCount}</span>
              )}
            </Button>
          </div>
        }
      />

      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("producers.search")}
          className="pl-11 py-3.5 text-base"
          data-testid="producers-search"
        />
      </div>

      {showFilters && (
        <Card className="p-5 mb-4 animate-fade-up" data-testid="filter-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-semibold text-ink-900 flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-electric-600" />
              {t("producers.filters")}
            </div>
            <button onClick={reset} data-testid="filter-reset" className="text-xs text-electric-600 hover:text-electric-500 flex items-center gap-1">
              <X className="w-3 h-3" /> {t("producers.clear")}
            </button>
          </div>
          <div className="space-y-4">
            <FilterGroup title={t("producers.skill")} items={facets.skills.slice(0, 8)} active={filters.skills} keyName="skills" onToggle={toggleIn} />
            <FilterGroup title={t("producers.material")} items={facets.materials.slice(0, 8)} active={filters.materials} keyName="materials" onToggle={toggleIn} />
            <FilterGroup title={t("producers.certification")} items={facets.certs.slice(0, 6)} active={filters.certs} keyName="certs" onToggle={toggleIn} />
            <div className="grid sm:grid-cols-3 gap-4 pt-2 border-t border-carbon-600">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-2">{t("producers.location")}</div>
                <select data-testid="filter-location" value={filters.location || ""} onChange={(e) => setFilters({ ...filters, location: e.target.value || null })} className="w-full bg-carbon-800 border border-carbon-600 rounded-lg px-3 py-2 text-xs text-ink-900">
                  <option value="">{t("producers.any")}</option>
                  {facets.locations.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-2">{t("producers.availability")}</div>
                <select data-testid="filter-availability" value={filters.availability || ""} onChange={(e) => setFilters({ ...filters, availability: e.target.value || null })} className="w-full bg-carbon-800 border border-carbon-600 rounded-lg px-3 py-2 text-xs text-ink-900">
                  <option value="">{t("producers.any")}</option>
                  {AVAILABILITIES.map((a) => <option key={a} value={a}>{t(`availability.${a}`)}</option>)}
                </select>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-2">
                  {t("producers.minReputation")}: <span className="font-mono text-ink-900">{filters.minRep}</span>
                </div>
                <input type="range" min="0" max="95" step="5" data-testid="filter-min-rep" value={filters.minRep} onChange={(e) => setFilters({ ...filters, minRep: Number(e.target.value) })} className="w-full accent-electric-500" />
              </div>
            </div>
          </div>
        </Card>
      )}

      {compare.length > 0 && (
        <div className="sticky top-2 z-20 mb-4 animate-fade-up">
          <Card className="p-3 flex items-center gap-3 border-electric-500/40" data-testid="compare-bar">
            <GitCompare className="w-4 h-4 text-electric-600 shrink-0" />
            <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
              {compare.map((id) => {
                const p = producers.find((x) => x.id === id);
                return (
                  <span key={id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-electric-500/10 border border-electric-500/40 text-electric-600 text-xs">
                    {p?.name}
                    <button onClick={() => toggleCompare(id)} data-testid={`unpick-${id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
              <span className="text-[11px] text-ink-500 font-mono">{compare.length}/3</span>
            </div>
            <Button onClick={() => navigate(`/app/compare?ids=${compare.join(",")}`)} data-testid="open-compare" className="shrink-0">
              <GitCompare className="w-4 h-4" />
              {t("producers.openCompare")}
            </Button>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="text-ink-500 font-mono text-xs py-8 text-center">Loading…</div>
      ) : list.length === 0 ? (
        <Card className="p-10 text-center" data-testid="producers-empty">
          <p className="text-ink-700 text-sm">{t("producers.emptyTitle")}</p>
          <p className="text-ink-500 text-xs mt-1">{t("producers.emptyHint")}</p>
        </Card>
      ) : (
        <Card className="divide-y divide-carbon-600" data-testid="producers-list">
          {list.map((p) => {
            const inCompare = compare.includes(p.id);
            return (
              <div key={p.id} data-testid={`producer-row-${p.id}`}
                className="flex items-center gap-3 p-3 sm:p-4 hover:bg-carbon-800/40 transition">
                <button
                  onClick={() => navigate(`/app/producers/${p.id}`)}
                  data-testid={`open-${p.id}`}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-carbon-750 border border-carbon-600 flex items-center justify-center font-mono text-electric-600 text-xs shrink-0">
                    {(p.name || "").split(" ").map((w) => w?.[0] || "").filter(Boolean).slice(0, 2).join("") || "•"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-medium text-ink-900 truncate">{p.name || t("visibility.badge")}</span>
                      <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-carbon-750 border border-carbon-600 text-ink-600 shrink-0">
                        {p.userType === "person" ? "PERSON" : "ORG"}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-600 flex-wrap">
                      <span className="flex items-center gap-1">
                        <StatusDot status={p.availability} />
                        {t(`availability.${p.availability}`)}
                      </span>
                      {p.location && (
                        <>
                          <span className="text-ink-500">·</span>
                          <span className="flex items-center gap-1 truncate max-w-[160px]">
                            <MapPin className="w-3 h-3 shrink-0" /> {p.location}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
                <div className="hidden sm:block shrink-0">
                  <ReputationBadge score={p.reputation} />
                </div>
                <button
                  onClick={() => toggleCompare(p.id)}
                  data-testid={`compare-toggle-${p.id}`}
                  disabled={!inCompare && compare.length >= 3}
                  title={inCompare ? t("producers.removeCompare") : t("producers.addCompare")}
                  className={`hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-md text-xs transition shrink-0 ${
                    inCompare
                      ? "bg-electric-500/15 border border-electric-500/40 text-electric-600"
                      : compare.length >= 3
                        ? "border border-carbon-600 text-ink-500 opacity-40 cursor-not-allowed"
                        : "border border-carbon-600 text-ink-700 hover:border-electric-500 hover:text-electric-600"
                  }`}
                >
                  <GitCompare className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => navigate(`/app/producers/${p.id}`)}
                  data-testid={`open-arrow-${p.id}`}
                  aria-label={t("producers.view")}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md text-ink-500 hover:text-ink-900 hover:bg-carbon-750 shrink-0"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}
